import { useState, useEffect, useCallback } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { API } from '../../config/api';
import { toast } from 'sonner';
import {
  TrendingUp, RefreshCw, Music, Video, Star, Mic, Radio,
  Headphones, Building2, Users, FileText, Sparkles, Clock,
  DollarSign, Loader2, Search, Shield, ChevronDown, ChevronUp,
  CreditCard, AlertTriangle, CheckCircle, XCircle
} from 'lucide-react';

const SLOT_ICONS = {
  top_song_week: Music,
  top_video_week: Video,
  featured_artist_week: Star,
  artist_on_rise_week: TrendingUp,
  top_podcaster_week: Mic,
  top_streamer_week: Radio,
  top_producer_week: Headphones,
  top_studio_week: Building2,
  community_spotlight_week: Users,
  top_story_week: FileText,
  spotlight_interview_week: Mic,
  soundstage_spotlight_paid: Sparkles,
};

const SLOT_COLORS = {
  top_song_week: 'border-orange-500/40 bg-orange-500/5',
  top_video_week: 'border-red-500/40 bg-red-500/5',
  featured_artist_week: 'border-purple-500/40 bg-purple-500/5',
  artist_on_rise_week: 'border-emerald-500/40 bg-emerald-500/5',
  top_podcaster_week: 'border-blue-500/40 bg-blue-500/5',
  top_streamer_week: 'border-pink-500/40 bg-pink-500/5',
  top_producer_week: 'border-violet-500/40 bg-violet-500/5',
  top_studio_week: 'border-amber-500/40 bg-amber-500/5',
  community_spotlight_week: 'border-teal-500/40 bg-teal-500/5',
  top_story_week: 'border-sky-500/40 bg-sky-500/5',
  spotlight_interview_week: 'border-indigo-500/40 bg-indigo-500/5',
  soundstage_spotlight_paid: 'border-yellow-500/40 bg-yellow-500/5',
};

export default function AutoTrendingManager({ getAuthHeaders }) {
  const [slots, setSlots] = useState({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [stripeDashboard, setStripeDashboard] = useState(null);
  const [stripeTransactions, setStripeTransactions] = useState([]);
  const [promotions, setPromotions] = useState([]);
  const [overrideSlot, setOverrideSlot] = useState(null);
  const [overrideId, setOverrideId] = useState('');
  const [showTransactions, setShowTransactions] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [slotsRes, stripeRes, txnRes, promoRes] = await Promise.all([
        fetch(`${API}/featured-slots`),
        fetch(`${API}/admin/stripe/dashboard`, { headers: getAuthHeaders() }),
        fetch(`${API}/admin/stripe/transactions?limit=10`, { headers: getAuthHeaders() }),
        fetch(`${API}/admin/promotions`, { headers: getAuthHeaders() }),
      ]);
      if (slotsRes.ok) setSlots((await slotsRes.json()).slots || {});
      if (stripeRes.ok) setStripeDashboard(await stripeRes.json());
      if (txnRes.ok) setStripeTransactions((await txnRes.json()).transactions || []);
      if (promoRes.ok) setPromotions((await promoRes.json()).promotions || []);
    } catch (err) {
      console.error('Failed to fetch auto-trending data:', err);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleForceRefresh = async () => {
    setRefreshing(true);
    try {
      const res = await fetch(`${API}/admin/stats/force-refresh`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        toast.success('Trending data refreshed');
        await fetchData();
      } else toast.error('Refresh failed');
    } catch { toast.error('Refresh failed'); }
    finally { setRefreshing(false); }
  };

  const handleOverride = async (slotKey) => {
    if (!overrideId.trim()) return;
    try {
      const res = await fetch(`${API}/admin/featured-slots/${slotKey}/override`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ target_id: overrideId.trim() }),
      });
      if (res.ok) {
        toast.success(`Slot "${slotKey}" overridden`);
        setOverrideSlot(null);
        setOverrideId('');
        await fetchData();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Override failed');
      }
    } catch { toast.error('Override failed'); }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
    </div>
  );

  const filledSlots = Object.entries(slots).filter(([, s]) => s.selected_id);
  const emptySlots = Object.entries(slots).filter(([, s]) => !s.selected_id);

  return (
    <div className="space-y-8" data-testid="auto-trending-manager">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <TrendingUp className="w-6 h-6 text-secondary" />
            Auto-Trending Engine
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Manage algorithmic featured slots, paid promotions, and Stripe transactions
          </p>
        </div>
        <Button
          onClick={handleForceRefresh}
          disabled={refreshing}
          variant="outline"
          data-testid="force-refresh-btn"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          {refreshing ? 'Refreshing...' : 'Force Refresh'}
        </Button>
      </div>

      {/* Stripe Dashboard Summary */}
      {stripeDashboard && (
        <Card className="p-5 border-border" data-testid="stripe-dashboard-card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold flex items-center gap-2">
              <CreditCard className="w-5 h-5" />
              Stripe Overview
            </h3>
            <Badge className={stripeDashboard.mode === 'LIVE' ? 'bg-green-500' : 'bg-yellow-500'}>
              {stripeDashboard.mode}
            </Badge>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
            {Object.entries(stripeDashboard.revenue || {}).map(([key, val]) => (
              <div key={key} className="text-center p-3 rounded-lg bg-muted/30">
                <p className="text-xs text-muted-foreground capitalize">{key.replace(/_/g, ' ')}</p>
                <p className="text-lg font-bold">${val.total}</p>
                <p className="text-xs text-muted-foreground">{val.count} txns</p>
              </div>
            ))}
          </div>
          {(stripeDashboard.pending?.transactions > 0 || stripeDashboard.pending?.donations > 0) && (
            <div className="mt-3 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-yellow-400" />
              <span className="text-sm text-yellow-400">
                {stripeDashboard.pending.transactions + stripeDashboard.pending.donations + stripeDashboard.pending.coin_purchases} pending transactions
              </span>
            </div>
          )}
        </Card>
      )}

      {/* Featured Slots Grid */}
      <div>
        <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-yellow-400" />
          Featured Slots ({filledSlots.length}/12 Active)
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Object.entries(slots).map(([key, slot]) => {
            const Icon = SLOT_ICONS[key] || Star;
            const colorClass = SLOT_COLORS[key] || 'border-border';
            const meta = slot.selected_meta || {};
            const isOverriding = overrideSlot === key;

            return (
              <Card
                key={key}
                className={`p-4 border ${colorClass} transition-all`}
                data-testid={`slot-card-${key}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Icon className="w-4 h-4" />
                    <span className="font-bold text-sm uppercase">{slot.label || key.replace(/_/g, ' ')}</span>
                  </div>
                  <Badge variant="outline" className={`text-xs ${
                    slot.selection_mode === 'paid' ? 'text-yellow-400 border-yellow-400/40' :
                    slot.selection_mode === 'admin_override' ? 'text-blue-400 border-blue-400/40' :
                    slot.selected_id ? 'text-green-400 border-green-400/40' : 'text-muted-foreground'
                  }`}>
                    {slot.selection_mode === 'paid' ? 'Paid' :
                     slot.selection_mode === 'admin_override' ? 'Manual' :
                     slot.selected_id ? 'Auto' : 'Empty'}
                  </Badge>
                </div>

                {slot.selected_id ? (
                  <div className="space-y-1 mb-3">
                    <p className="text-sm font-medium truncate">{meta.title || slot.selected_id}</p>
                    {meta.handle && <p className="text-xs text-muted-foreground">@{meta.handle}</p>}
                    {slot.end_at && (
                      <p className="text-xs text-muted-foreground flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        Expires: {new Date(slot.end_at).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground mb-3">No content selected — will auto-fill on next refresh</p>
                )}

                {isOverriding ? (
                  <div className="space-y-2">
                    <Input
                      placeholder="Enter track_id, user_id, or video_id"
                      value={overrideId}
                      onChange={(e) => setOverrideId(e.target.value)}
                      className="h-8 text-xs"
                      data-testid={`override-input-${key}`}
                    />
                    <div className="flex gap-2">
                      <Button size="sm" className="h-7 text-xs" onClick={() => handleOverride(key)} data-testid={`override-confirm-${key}`}>
                        <CheckCircle className="w-3 h-3 mr-1" /> Apply
                      </Button>
                      <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => { setOverrideSlot(null); setOverrideId(''); }}>
                        <XCircle className="w-3 h-3 mr-1" /> Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Button
                    size="sm"
                    variant="outline"
                    className="w-full h-7 text-xs"
                    onClick={() => setOverrideSlot(key)}
                    data-testid={`override-btn-${key}`}
                  >
                    <Shield className="w-3 h-3 mr-1" /> Manual Override
                  </Button>
                )}
              </Card>
            );
          })}
        </div>
      </div>

      {/* Recent Transactions */}
      <div>
        <button
          className="font-bold text-lg mb-4 flex items-center gap-2 hover:text-primary transition-colors"
          onClick={() => setShowTransactions(!showTransactions)}
          data-testid="toggle-transactions"
        >
          <DollarSign className="w-5 h-5 text-green-400" />
          Recent Stripe Transactions ({stripeTransactions.length})
          {showTransactions ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
        
        {showTransactions && (
          <div className="space-y-2">
            {stripeTransactions.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">No transactions yet</p>
            ) : stripeTransactions.map((txn, i) => (
              <Card key={i} className="p-3 border-border flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Badge variant="outline" className="text-xs capitalize">
                    {txn._type || txn.type || 'unknown'}
                  </Badge>
                  <div>
                    <p className="text-sm font-medium">
                      ${txn.amount || txn.price_usd || 0}
                      {txn.tier && <span className="text-muted-foreground ml-1">({txn.tier})</span>}
                    </p>
                    <p className="text-xs text-muted-foreground">{txn.user_id || txn.purchaser_user_id || ''}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className={`text-xs ${
                    (txn.payment_status || txn.status) === 'paid' || (txn.payment_status || txn.status) === 'completed'
                      ? 'bg-green-500/20 text-green-400'
                      : (txn.payment_status || txn.status) === 'pending' || (txn.payment_status || txn.status) === 'initiated'
                      ? 'bg-yellow-500/20 text-yellow-400'
                      : 'bg-red-500/20 text-red-400'
                  }`}>
                    {txn.payment_status || txn.status || 'unknown'}
                  </Badge>
                  <span className="text-xs text-muted-foreground">
                    {txn.created_at ? new Date(txn.created_at).toLocaleDateString() : ''}
                  </span>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
